import { useCustomerSearch as useCustomersSearchQuery } from "@/modules/customers/presentation/api/queries";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  MarkReturnedDTO,
  ShippingBranchOptionsQueryDTO,
  UpdateOrderDetailsDTO,
} from "@/modules/orders/domain/contracts";
import type { OrderDetailResult } from "@/modules/orders/domain/types";
import { productsApi } from "@/modules/products/presentation/api/client";
import { ordersApi, type OrderListQuery } from "./client";

const keys = {
  all: ["orders"] as const,
  list: (query?: OrderListQuery) => [...keys.all, "list", query] as const,
  detail: (id: string) => [...keys.all, "detail", id] as const,
  shippingBranchOptions: (query: ShippingBranchOptionsQueryDTO) =>
    [...keys.all, "shipping-branch-options", query] as const,
};

export function useProductPickerQuery(query?: {
  categoryId?: string;
  search?: string;
  purpose?: "order" | "receive";
  excludeOrderId?: string;
}) {
  return useQuery({
    queryKey: ["products", "picker", query],
    queryFn: () => productsApi.picker(query),
  });
}

export function useCustomerSearch(q: string) {
  return useCustomersSearchQuery(q);
}

export function useShippingBranchOptionsQuery(
  query: ShippingBranchOptionsQueryDTO,
  enabled = true,
) {
  return useQuery({
    queryKey: keys.shippingBranchOptions(query),
    queryFn: () => ordersApi.shippingBranchOptions(query),
    enabled: enabled && Boolean(query.carrier.trim()),
  });
}

export function useOrdersQuery(query?: OrderListQuery) {
  return useQuery({
    queryKey: keys.list(query),
    queryFn: () => ordersApi.list(query),
  });
}

export function useOrderQuery(id: string) {
  return useQuery({
    queryKey: keys.detail(id),
    queryFn: () => ordersApi.get(id),
    enabled: Boolean(id),
  });
}

export function useCreateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOrderDTO) => ordersApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["restock-waitlist"] });
    },
  });
}

export function useUpdateOrder(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateOrderDTO) => ordersApi.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useMarkOrderPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ordersApi.markPaid(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

function useOrderTransition(
  mutationFn: (id: string, input?: unknown) => Promise<OrderDetailResult>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input?: unknown;
    }) => mutationFn(id, input),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["fulfillment"] });
    },
  });
}

export function useMarkOrderReady() {
  return useOrderTransition((id) => ordersApi.markReady(id));
}

export function useMarkOrderShipped() {
  return useOrderTransition((id, input) =>
    ordersApi.markShipped(id, input as { trackingNumber?: string | null }),
  );
}

export function useMarkOrderDelivered() {
  return useOrderTransition((id) => ordersApi.markDelivered(id));
}

export function useMarkOrderPickedUp() {
  return useOrderTransition((id) => ordersApi.markPickedUp(id));
}

export function useMarkOrderReturned() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input?: MarkReturnedDTO;
    }) => ordersApi.markReturned(id, input),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["fulfillment"] });
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useRevertOrderStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ordersApi.revertStatus(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["fulfillment"] });
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useReopenOrderForShipment() {
  return useOrderTransition((id) => ordersApi.reopenForShipment(id));
}

export function useUpdateOrderShipping() {
  return useOrderTransition((id, input) =>
    ordersApi.updateShipping(
      id,
      input as {
        shippingCarrier?: string | null;
        shippingBranch?: string | null;
        trackingNumber?: string | null;
      },
    ),
  );
}

export function useUpdateOrderDetails() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: UpdateOrderDetailsDTO;
    }) => ordersApi.updateDetails(id, input),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["fulfillment"] });
    },
  });
}

export function useCancelOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ordersApi.cancel(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}
